import type { RequestType } from "@/services/returns";

/** Canonical labels for return request types, reasons and statuses.
 *  Single source of truth shared by admin pages so the wording never drifts. */

export const REQUEST_TYPE_LABEL: Record<string, string> = {
  mijenja: "Mijenja",
  vraca: "Vraća",
  mijenja_dodatno: "Mijenja + naručuje dodatno",
};

export const REASON_LABEL: Record<string, string> = {
  preveliko: "Preveliko",
  premalo: "Premalo",
  kvalitet: "Kvalitet proizvoda",
  razlikuje_se: "Razlikuje se od proizvoda na slici",
  pogresan: "Pogrešan proizvod",
  sa_greskom: "Proizvod sa greškom",
  kasna_isporuka: "Kasna isporuka",
  ne_stoji: "Ne stoji mi dobro",
};

export const STATUS_LABEL: Record<string, string> = {
  new: "Novo",
  in_progress: "U obradi",
  approved: "Odobreno",
  rejected: "Odbijeno",
  refund_completed: "Povrat izvršen",
  exchange_completed: "Zamjena izvršena",
};

/** Translucent badge styling used in list/table views. */
export const STATUS_BADGE: Record<string, string> = {
  new: "bg-blue-500/15 text-blue-400 border-blue-500/20",
  in_progress: "bg-yellow-500/15 text-yellow-400 border-yellow-500/20",
  approved: "bg-green-500/15 text-green-400 border-green-500/20",
  rejected: "bg-red-500/15 text-red-400 border-red-500/20",
  refund_completed: "bg-violet-500/15 text-violet-400 border-violet-500/20",
  exchange_completed: "bg-teal-500/15 text-teal-400 border-teal-500/20",
};

/** Neriješen zahtjev star ovoliko dana (ili više) se smatra "starim" — vidi
 *  ReturnService.list({ stale }), staleCount() i crvenu oznaku u listi zahtjeva. */
export const STALE_DAYS = 16;

/** Statusi koji znače "zahtjev još nije zatvoren". Jedino mjesto istine — dijele ga
 *  server (SQL uslov za stare zahtjeve) i klijent (crvena oznaka u listi), pa dodavanje
 *  novog statusa ne može razdvojiti brojač od oznake. */
export const UNRESOLVED_STATUSES = ["new", "in_progress", "approved"];

const UNRESOLVED_STATUS_SET = new Set(UNRESOLVED_STATUSES);

/** True kad zahtjev sa ovim statusom još nije riješen. */
export function isUnresolvedStatus(status: string): boolean {
  return UNRESOLVED_STATUS_SET.has(status);
}

/** True when the request is an exchange (replacement) rather than a refund. */
export function isExchangeRequest(requestType: string | RequestType): boolean {
  return requestType === "mijenja" || requestType === "mijenja_dodatno";
}

/** Bosanska deklinacija za broj dana: zadnja cifra 1 (a nije 11) → "dan", inače "dana". */
export function formatDaysAgo(days: number): string {
  const lastDigit = days % 10;
  const lastTwoDigits = days % 100;
  const word = lastDigit === 1 && lastTwoDigits !== 11 ? "dan" : "dana";
  return `${days} ${word}`;
}
